<script setup lang="ts">
/** Checklist for Financial Year — SG Incorporation (Post) */
import { ref } from 'vue';
import CddVarField from '../incorporation-pre/cdd/CddVarField.vue';
import './allotDoc.css';

type AuditorOpt = 'external' | 'csp_arrange';
const auditorOpt = ref<AuditorOpt>('external');
const bookkeeper = ref(false);
const gst = ref(false);
const cr = ref(false);
const license = ref(false);
function mark(on: boolean) { return on ? '☑' : '☐'; }
function audMark(v: AuditorOpt) { return auditorOpt.value === v ? '☑' : '☐'; }
</script>

<template>
  <div class="allot-doc">
    <div class="doc-head">
      <div class="doc-lbl-title" style="text-align:center; font-size:15px">CHECKLIST FOR FINANCIAL YEAR</div>
    </div>

    <table class="cdd-tbl">
      <tbody>
        <tr><td class="cdd-lcell">To</td><td><CddVarField field-key="csp_name" label="CSP Name" /></td></tr>
        <tr><td class="cdd-lcell">From</td><td><CddVarField field-key="company_name" label="Company Name" /></td></tr>
        <tr><td class="cdd-lcell">Date</td><td><CddVarField field-key="letter_date" label="Letter Date" /></td></tr>
        <tr><td class="cdd-lcell">Subject</td><td>Checklist for Financial Year</td></tr>
      </tbody>
    </table>

    <p class="doc-p" style="margin-top:12px">I / We hereby confirm the following matters:</p>

    <p class="doc-sec-title" style="text-align:left">1. FINANCIAL YEAR END</p>
    <p class="doc-p">
      We would like to fix the financial year-end to be on
      <CddVarField field-key="financial_year_end" label="Financial Year End" />.
    </p>
    <p class="doc-p" style="font-size:11.5px; color:#444">
      FORM C (Corporate Tax Computation): This must be submitted to IRAS by the end of November every year for
      the preceding financial year. If you do NOT receive the Form C from IRAS by the end of June every year,
      please notify us or IRAS.
    </p>

    <p class="doc-sec-title" style="text-align:left">2. AUDITOR TO BE APPOINTED</p>
    <p class="doc-p">
      <span class="doc-toggle" @click="auditorOpt = 'external'">{{ audMark('external') }} We are appointing an external auditor</span>
    </p>
    <template v-if="auditorOpt === 'external'">
      <p class="doc-p">
        We are appointing <CddVarField field-key="auditor_firm_name" label="Auditor Firm Name" /> to be our auditor.<br />
        Audit firm's address: <CddVarField field-key="auditor_firm_address" label="Auditor Firm Address" />
      </p>
    </template>
    <p class="doc-p">
      <span class="doc-toggle" @click="auditorOpt = 'csp_arrange'">{{ audMark('csp_arrange') }} We would like the Service Provider to arrange for an Auditor for our company.</span>
    </p>
    <p class="doc-p" style="font-size:11.5px; color:#444">
      Small exempt companies in Singapore (where shareholders are individuals, and annual turnover is less
      than S$5 Million) are no longer required to audit their accounts, but are still required to prepare a
      complete set of Directors' Report according to Singapore Financial Reporting Standards.
    </p>

    <p class="doc-sec-title" style="text-align:left">3. BOOKKEEPER / TAX SERVICES</p>
    <p class="doc-p">
      <span class="doc-toggle" @click="bookkeeper = !bookkeeper">{{ mark(bookkeeper) }} We would like the Service Provider to arrange bookkeeper / tax services for our company.</span>
    </p>

    <p class="doc-sec-title" style="text-align:left">4. OPENING OF BANK ACCOUNT</p>
    <table class="cdd-tbl">
      <tbody>
        <tr><td class="cdd-lcell">Bank</td><td><CddVarField field-key="bank_name" label="Bank Name" /></td></tr>
        <tr><td class="cdd-lcell">Branch</td><td><CddVarField field-key="bank_branch" label="Bank Branch" /></td></tr>
        <tr><td class="cdd-lcell">Manner of Operation</td><td><CddVarField field-key="bank_manner_of_operation" label="Manner of Operation" /></td></tr>
      </tbody>
    </table>
    <ul class="sg-bullets">
      <li>Clients may be requested to provide us with the Bank account application form.</li>
      <li>A Directors' Resolution is required for the opening of a bank account.</li>
      <li>All directors and authorised signatories need to sign the resolution.</li>
      <li>The Company Secretary will certify the resolution only after the directors and signatories sign all necessary documents.</li>
    </ul>

    <p class="doc-sec-title" style="text-align:left">5. ANY OTHER MATTER(S)</p>
    <p class="doc-p">
      <span class="doc-toggle" @click="gst = !gst">{{ mark(gst) }} GST Application</span>
    </p>
    <p class="doc-p">
      <span class="doc-toggle" @click="cr = !cr">{{ mark(cr) }} CR (Customs Registration) Application</span>
    </p>
    <p class="doc-p">
      <span class="doc-toggle" @click="license = !license">{{ mark(license) }} LICENSE Application</span>
      <template v-if="license">
        — Please specify: <CddVarField field-key="license_specify" label="License Specify" />
      </template>
    </p>

    <p class="doc-p" style="margin-top:16px">Thank you.</p>
    <div class="sig-block">
      <div class="sig-line">____________________________________</div>
      <div>Name: <CddVarField field-key="signatory_name" label="Signatory Name" /></div>
      <div>for and on behalf of <CddVarField field-key="company_name" label="Company Name" /></div>
      <div>Company Registration No.: <CddVarField field-key="company_registration_no" label="Registration No." /></div>
    </div>
  </div>
</template>

<style scoped>
.sg-bullets { margin: 8px 0 10px; padding-left: 22px; font-size: 12px; line-height: 1.5; color: #444; }
</style>
